import React from 'react';

import { translate } from '~l10n/client';

import { UIButton } from '~shared/components/buttons';
import { Dialog } from '~shared/components/Dialog';
import { Divider } from '~shared/components/Divider';
import { useAutoFocus } from '~shared/components/helpers';
import { Inline } from '~shared/components/Inline';
import TextInput from '~shared/components/TextInput';
import { toast } from '~shared/components/Toast';
import { useUpdateWorkspaceFolder } from '~teacher/hook/workspaces';

interface RenameWorkspaceFolderProps {
  workspaceId: string;
  workspaceFolderId: string;
  initialFolderName: string;
  isOpen: boolean;
  onClose: () => void;
}

export function RenameWorkspaceFolderDialog({
  workspaceId,
  initialFolderName,
  workspaceFolderId,
  isOpen,
  onClose,
}: RenameWorkspaceFolderProps) {
  const saveWorkspaceFolderName = useUpdateWorkspaceFolder();
  const onSaveFolder = ({
    newFolderName,
    onError,
    onSuccess,
  }: {
    newFolderName: string;
    onSuccess?: () => void;
    onError?: () => void;
  }) =>
    saveWorkspaceFolderName.mutate(
      {
        workspaceId: workspaceId,
        workspaceFolderId: workspaceFolderId,
        name: newFolderName,
      },
      {
        onSuccess: () => {
          onSuccess();
        },
        onError: () => {
          toast.critical(translate('An error has occurred'));
          onError();
        },
      }
    );

  return (
    <RenameFolderDialog
      isOpen={isOpen}
      onClose={onClose}
      initialFolderName={initialFolderName}
      onSaveFolder={onSaveFolder}
    />
  );
}

type onSaveFolderCallback = ({
  newFolderName,
  onError,
  onSuccess,
}: {
  newFolderName: string;
  onSuccess?: () => void;
  onError?: () => void;
}) => void;

interface RenameFolderDialogProps {
  isOpen: boolean;
  onClose: () => void;
  initialFolderName: string;
  onSaveFolder: onSaveFolderCallback;
}

/**
 * This is a general rename folder dialog. Since wrap this dialog with another
 * component, and you get customized behaviours
 * @TODO modify the personal folder rename dialog to use this component
 */
function RenameFolderDialog({
  onSaveFolder,
  initialFolderName,
  onClose,
  isOpen,
}: RenameFolderDialogProps) {
  const [newFolderName, setFolderName] = React.useState(initialFolderName);
  const [isSavingFolderName, setIsSavingFolderName] = React.useState(false);

  React.useEffect(() => {
    if (isOpen) {
      setFolderName(initialFolderName);
      setIsSavingFolderName(false);
    }
  }, [isOpen, initialFolderName, setFolderName]);

  const autoFocusOnOpenCallback = useAutoFocus();

  return (
    <Dialog
      onClose={onClose}
      id="rename-folder-modal"
      open={isOpen}
      title={translate('Rename Folder')}
      closeLabel={translate('Cancel')}
    >
      <div className="mt-5 mb-5">
        <Divider />
      </div>
      <div className="text-base leading-8">
        <label htmlFor="folder-name">{translate('Folder Name')}</label>
        <TextInput
          ref={autoFocusOnOpenCallback}
          maxLength={255}
          id="folder-name"
          value={newFolderName}
          onChange={(e) => setFolderName(e.target.value)}
          className="mt-3"
        />
      </div>
      <div className="pt-8">
        <Inline align="right">
          <UIButton onClick={onClose}>{translate('Cancel')}</UIButton>
          <UIButton
            isDisabled={isSavingFolderName}
            onClick={() => {
              setIsSavingFolderName(true);
              onSaveFolder({
                newFolderName: newFolderName,
                onSuccess: () => onClose(),
                onError: () => setIsSavingFolderName(false),
              });
            }}
            appearance="primary"
            a11yLabel="rename-folder-button"
          >
            {translate('Rename')}
          </UIButton>
        </Inline>
      </div>
    </Dialog>
  );
}
